import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaStar, FaUsers, FaChevronDown, FaCheckCircle,
  FaCalendarAlt, FaPhoneAlt, FaArrowRight, FaMoon,
} from "react-icons/fa";
import { GiPartyPopper } from "react-icons/gi";
import { MdCelebration, MdGroups, MdBusinessCenter } from "react-icons/md";

/* ── Event Category Data ── */
const eventCategories = [
  {
    id: "group-day-long",
    title: "Group Day Long",
    subtitle: "A full-day escape for your entire group",
    icon: <MdGroups className="text-5xl" />,
    color: "from-emerald-500 to-teal-700",
    lightColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    accentColor: "text-emerald-600",
    badgeColor: "bg-emerald-600",
    description:
      "Treat your group to an extraordinary full-day experience at Chuti Resort Purbachal. Our Group Day Long package is designed to offer a perfect balance of leisure, adventure, and team bonding in a stunning natural setting.",
    details:
      "From morning activities and guided nature walks to poolside relaxation and a grand outdoor lunch, every moment of the day is thoughtfully curated to ensure your group leaves with lasting memories. Ideal for schools, colleges, corporate teams, and community groups.",
    includes: [
      "Welcome refreshments on arrival",
      "Guided nature walk and outdoor activities",
      "Swimming pool access",
      "Boat ride on the lake",
      "Buffet lunch with seasonal menu",
      "Evening snacks and tea",
      "Photography zone and props",
      "Dedicated group coordinator",
    ],
    capacity: "20 – 500 Guests",
    duration: "8:00 AM – 6:00 PM",
    tag: "Most Popular",
  },
  {
    id: "corporate-gathering",
    title: "Corporate Gathering",
    subtitle: "Strengthen bonds beyond the office",
    icon: <MdBusinessCenter className="text-5xl" />,
    color: "from-blue-500 to-blue-800",
    lightColor: "bg-blue-50",
    borderColor: "border-blue-200",
    accentColor: "text-blue-600",
    badgeColor: "bg-blue-600",
    description:
      "Create meaningful connections and boost team morale with a bespoke corporate gathering at Chuti Convention Centre. Our resort provides the ideal backdrop for productive team building and meaningful networking away from the office environment.",
    details:
      "Whether it is a casual team outing, a formal corporate dinner, or a hybrid indoor-outdoor event, our experienced team ensures every detail is perfectly handled. We offer customised menus, entertainment, and décor to align with your company's brand and event objectives.",
    includes: [
      "Dedicated event planning team",
      "Indoor or outdoor venue setup",
      "Customised catering menu",
      "Audio-visual equipment",
      "Team-building activity options",
      "Welcome gifts for attendees",
      "Professional photography",
      "Transport coordination (on request)",
    ],
    capacity: "30 – 300 Guests",
    duration: "Half-day or Full-day",
    tag: "Corporate",
  },
  {
    id: "agm-sales-meet",
    title: "AGM Sales Meet",
    subtitle: "Drive results in an inspiring venue",
    icon: <FaUsers className="text-5xl" />,
    color: "from-violet-500 to-purple-800",
    lightColor: "bg-violet-50",
    borderColor: "border-violet-200",
    accentColor: "text-violet-600",
    badgeColor: "bg-violet-600",
    description:
      "Host your Annual General Meeting or Sales Meet at Chuti Convention Centre — a professional, fully equipped venue that sets the stage for impactful presentations, strategic discussions, and motivated sales teams.",
    details:
      "Our AGM and Sales Meet packages are tailored to deliver a seamless, professional experience. From state-of-the-art audio-visual setups and high-speed internet to on-demand catering and comfortable breakout rooms, everything you need is right here. Boost team motivation and alignment in an inspiring environment.",
    includes: [
      "Main conference hall or seminar room",
      "Full AV system with HD projector",
      "High-speed WiFi throughout",
      "Registration desk setup",
      "Tea, coffee, and refreshment breaks",
      "Lunch and dinner arrangements",
      "Breakout rooms for group sessions",
      "Printed materials support (on request)",
    ],
    capacity: "50 – 500 Guests",
    duration: "Full-day or Multi-day",
    tag: "Business",
  },
  {
    id: "dealer-meet",
    title: "Dealer Meet",
    subtitle: "Celebrate your partners in style",
    icon: <MdCelebration className="text-5xl" />,
    color: "from-orange-500 to-amber-700",
    lightColor: "bg-orange-50",
    borderColor: "border-orange-200",
    accentColor: "text-orange-600",
    badgeColor: "bg-orange-600",
    description:
      "Recognise and reward your valued dealers with an exclusive Dealer Meet at Chuti Resort Purbachal. Create a memorable event that strengthens relationships, showcases new products, and motivates your dealer network.",
    details:
      "Our Dealer Meet packages are designed to impress. From a grand welcome reception and product exhibition spaces to gala dinners and overnight accommodation options, we craft an experience that makes every dealer feel valued and inspired. Leave a lasting impression that translates into long-term business loyalty.",
    includes: [
      "Welcome reception with refreshments",
      "Product showcase and exhibition space",
      "Conference hall for presentations",
      "Gala dinner arrangement",
      "Cultural entertainment programme",
      "Branded décor and stage setup",
      "Overnight accommodation (on request)",
      "Personalised gifts and mementos",
    ],
    capacity: "50 – 400 Guests",
    duration: "1 – 3 Days",
    tag: "Premium",
  },
  {
    id: "annual-agm",
    title: "Annual AGM",
    subtitle: "Your most important meeting, perfected",
    icon: <FaCalendarAlt className="text-5xl" />,
    color: "from-slate-500 to-slate-800",
    lightColor: "bg-slate-50",
    borderColor: "border-slate-200",
    accentColor: "text-slate-600",
    badgeColor: "bg-slate-700",
    description:
      "Conduct your Annual General Meeting (AGM) with the gravitas it deserves at the Chuti Convention Centre. Our grand banquet hall and professional support team ensure your AGM is conducted with precision, efficiency, and professionalism.",
    details:
      "From voting and resolution management to formal presentations and board dinner arrangements, we provide comprehensive support to make your AGM a smooth and dignified affair. Our venue accommodates large gatherings while offering the privacy and formality required for your organisation's most critical annual event.",
    includes: [
      "Grand banquet hall setup",
      "Professional stage and podium",
      "Registration and seating management",
      "Full audio-visual system with live feed",
      "Secretariat support area",
      "Formal lunch or dinner",
      "Video recording facility (on request)",
      "VIP lounge for board members",
    ],
    capacity: "100 – 500 Guests",
    duration: "Half-day or Full-day",
    tag: "Formal",
  },
  {
    id: "night-stay",
    title: "Night Stay",
    subtitle: "An overnight retreat in nature's embrace",
    icon: <FaMoon className="text-5xl" />,
    color: "from-indigo-500 to-violet-800",
    lightColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    accentColor: "text-indigo-600",
    badgeColor: "bg-indigo-600",
    description:
      "Extend your experience at Chuti Resort Purbachal with a relaxing overnight stay. Our Night Stay package offers a seamless blend of comfort, nature, and resort amenities — perfect for groups, families, and couples seeking a peaceful escape from city life.",
    details:
      "Wake up to birdsong and morning mist over the lake, enjoy a hearty breakfast, and make the most of all resort facilities at your own pace. Our accommodation options range from cosy twin rooms to spacious family suites and private cottages, ensuring a restful and memorable night for every guest.",
    includes: [
      "Overnight accommodation in chosen room type",
      "Complimentary breakfast for all guests",
      "Swimming pool access (morning & evening)",
      "Evening bonfire arrangement (on request)",
      "Boat ride on the resort lake",
      "Welcome refreshments on arrival",
      "Room service available",
      "Late check-out option (subject to availability)",
    ],
    capacity: "2 – 200 Guests",
    duration: "Check-in 2:00 PM / Check-out 12:00 Noon",
    tag: "Overnight",
  },
  {
    id: "destination-wedding",
    title: "Destination Wedding",
    subtitle: "Say 'I do' in paradise",
    icon: <GiPartyPopper className="text-5xl" />,
    color: "from-rose-400 to-pink-700",
    lightColor: "bg-rose-50",
    borderColor: "border-rose-200",
    accentColor: "text-rose-600",
    badgeColor: "bg-rose-600",
    description:
      "Make your most special day truly magical at Chuti Resort Purbachal. Our destination wedding packages are crafted to deliver a fairy-tale experience, combining the natural beauty of Purbachal with world-class hospitality and meticulous attention to detail.",
    details:
      "Imagine exchanging vows surrounded by lush greenery and shimmering water, followed by an elegant banquet under the stars. Our dedicated wedding planning team handles everything — from floral décor and catering to entertainment and guest accommodation — so you can simply savour every moment of your special day.",
    includes: [
      "Exclusive venue hire (indoor or outdoor)",
      "Bridal suite and groom's suite",
      "Customised floral décor and stage design",
      "Multi-course wedding banquet",
      "Professional wedding coordinator",
      "Live music or DJ arrangements",
      "Accommodation for wedding party",
      "Pre-wedding photoshoot location",
    ],
    capacity: "50 – 500 Guests",
    duration: "1 – 3 Days",
    tag: "Exclusive",
  },
];

/* ── Accordion Item ── */
function EventCard({ event, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      className={`rounded-2xl overflow-hidden shadow-md border ${event.borderColor} bg-white`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
    >
      {/* Header */}
      <div
        className={`bg-gradient-to-r ${event.color} p-6 md:p-8 text-white cursor-pointer select-none`}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <div className="bg-white/20 rounded-2xl p-3 flex-shrink-0">
              {event.icon}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-2xl md:text-3xl font-fancy font-bold">{event.title}</h3>
                <span className={`${event.badgeColor} bg-white/25 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide`}>
                  {event.tag}
                </span>
              </div>
              <p className="text-white/75 text-sm mt-1">{event.subtitle}</p>
              <div className="flex flex-wrap gap-4 mt-2 text-white/80 text-xs">
                <span>👥 {event.capacity}</span>
                <span>⏱ {event.duration}</span>
              </div>
            </div>
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <FaChevronDown className="text-white/80 text-xl" />
          </motion.div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left – Description */}
                <div>
                  <p className={`${event.accentColor} uppercase tracking-widest text-xs font-bold mb-3`}>
                    Overview
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">{event.description}</p>
                  <p className="text-gray-500 leading-relaxed text-sm">{event.details}</p>

                  {/* Image placeholder */}
                  <div className={`mt-6 rounded-2xl ${event.lightColor} border-2 border-dashed ${event.borderColor} h-52 flex flex-col items-center justify-center text-center p-4`}>
                    <FaStar className={`${event.accentColor} text-3xl mb-3 opacity-40`} />
                    <p className={`${event.accentColor} font-semibold text-sm`}>Event Photos Coming Soon</p>
                    <p className="text-gray-400 text-xs mt-1">Beautiful moments will be showcased here</p>
                  </div>
                </div>

                {/* Right – What's Included */}
                <div>
                  <p className={`${event.accentColor} uppercase tracking-widest text-xs font-bold mb-3`}>
                    What's Included
                  </p>
                  <ul className="space-y-3 mb-6">
                    {event.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                        <FaCheckCircle className={`${event.accentColor} flex-shrink-0 mt-0.5`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Info chips */}
                  <div className={`rounded-2xl ${event.lightColor} border ${event.borderColor} p-5`}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Capacity</p>
                        <p className="font-semibold text-gray-700">{event.capacity}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Duration</p>
                        <p className="font-semibold text-gray-700">{event.duration}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <a
                        href="/contact"
                        className={`inline-flex items-center gap-2 bg-gradient-to-r ${event.color} text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity`}
                      >
                        <FaPhoneAlt className="text-xs" />
                        Enquire Now
                        <FaArrowRight className="text-xs" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main Component ── */
function EventsActivities() {
  return (
    <div className="min-h-screen bg-stone-50">

      {/* ── Hero ── */}
      <div className="relative h-[58vh] min-h-[320px] overflow-hidden bg-gradient-to-br from-emerald-700 via-teal-700 to-blue-800">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <FaStar className="text-yellow-400 text-xl" />
              <span className="text-yellow-300 uppercase tracking-[0.3em] text-xs font-semibold">
                Create Unforgettable Moments
              </span>
              <FaStar className="text-yellow-400 text-xl" />
            </div>
            <h1 className="text-4xl md:text-6xl font-fancy mb-4 drop-shadow-lg">
              Events & Activities
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-xl mx-auto">
              From corporate milestones to dream weddings — we make every event extraordinary
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
              <div className="bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full">
                🎯 7 Event Types
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full">
                👨‍👩‍👧 All Group Sizes
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full">
                ✅ Fully Managed Events
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Intro Section ── */}
      <section className="max-w-4xl mx-auto px-4 py-14 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-emerald-600 uppercase tracking-widest text-xs font-bold">Why Choose Us</span>
          <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2 mb-4">
            Your Event, Our Expertise
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
            At Chuti Resort Purbachal, we believe every event deserves to be extraordinary. Our dedicated events team brings years of experience, creativity, and passion to every occasion — whether it is an intimate gathering or a grand celebration for hundreds of guests.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-10">
          {[
            { value: "7+", label: "Event Types", color: "text-emerald-600" },
            { value: "1500+", label: "Max Capacity", color: "text-blue-600" },
            { value: "365", label: "Days Open", color: "text-rose-600" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <p className={`text-4xl font-bold ${stat.color} font-fancy`}>{stat.value}</p>
              <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Event Categories ── */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-emerald-600 uppercase tracking-widest text-xs font-bold">Event Portfolio</span>
          <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2 mb-2">
            Our Event Categories
          </h2>
          <p className="text-gray-400 text-sm">Click on any event to explore details</p>
        </motion.div>

        <div className="space-y-5">
          {eventCategories.map((event, i) => (
            <EventCard key={event.id} event={event} index={i} />
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-gradient-to-br from-emerald-700 to-teal-800 py-16 px-4 text-white text-center">
        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-fancy mb-3">Ready to Plan Your Event?</h3>
          <p className="text-emerald-200 mb-7 max-w-lg mx-auto">
            Get in touch with our events team today. We would love to hear about your vision and help you bring it to life at Chuti Resort Purbachal.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contact"
              className="inline-block bg-white hover:bg-emerald-50 text-emerald-900 font-semibold px-8 py-3 rounded-full transition-colors duration-300 shadow-lg"
            >
              Contact Our Events Team
            </a>
            <a
              href="/rooms"
              className="inline-block bg-white/20 hover:bg-white/30 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300 border border-white/30"
            >
              View Accommodation
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

export default EventsActivities;
